import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from "react";
import { useSettingsState } from "../../../../context/settings";
import { useSwapDataState, useSwapDataUpdate } from "../../../../context/swap";
import ConnectNetwork from "../../../../components/Pages/Swap/Form/SecondaryComponents/ConnectNetwork";
import { generateSwapInitialValues, generateSwapInitialValuesFromSwap } from "../../../../lib/generateSwapInitialValues";
import { LSAPIKnownErrorCode } from "@layerswap/widget-types";
import { useInitialSettings } from "../../../../context/settings";
import useWallet from "../../../../hooks/useWallet";
import { useAsyncModal } from "../../../../context/asyncModal";
import VaulDrawer from "../../../../components/Modal/vaulModal";
import { useBalance } from "../../../../lib/balances/useBalance";
import { useSelectedAccount } from "../../../../context/swapAccounts";
// SwapDetails is the post-submit modal content. It transitively imports every
// Withdraw component (Withdraw, Processing, ManualWithdraw, Summary, Wallet
// button common, ...) which was the root of the ~100 KB Withdraw leak onto
// /. Wrapping in React.lazy moves that whole graph into its own chunk that
// only downloads when the user actually submits a swap and the drawer opens.
import { Suspense, lazy } from "react";
import { SwapDetailsSceleton } from "../../../../components/Common/Sceletons";
import { useCallbacks } from "../../../../context/callbackProvider";
import ContractAddressNote from "../../../../components/Input/Address/ContractAddressNote";
import { useContractAddressStore } from "../../../../stores/contractAddressStore";
import UrlAddressNote from "../../../../components/Input/Address/UrlAddressNote";
import { Address } from "../../../../lib/address/Address";
import ContractAddressValidationCache, { ContractSourceAddressValidationCache } from "./SecondaryComponents/validationError/ContractAddressValidationCache";
import { useGaslessPreferenceStore } from "../../../../stores/gaslessPreferenceStore";
import { FormQuoteReportProvider } from "../../../../context/formQuote";
import { isVerySlowRoute } from "../../../../lib/routeSpeed";
import SlowRouteNote from "./SecondaryComponents/SlowRouteNote";
import { lifecycleContextFromForm, lifecycleContextFromSwap, resolveFlowClosedEvent } from "../../../../lib/swapLifecycle";
import { useResolvedSwapStatus } from "../../../../hooks/useResolvedSwapStatus";
import SwapForm from './SwapForm';
const SwapDetails = lazy(() => import("../Withdraw/SwapDetails"));
export default function FormWrapper({ children, type, partner }) {
    const [showConnectNetworkModal, setShowConnectNetworkModal] = useState(false);
    const [isAddressFromQueryConfirmed, setIsAddressFromQueryConfirmed] = useState(false);
    const dontShowContractWarningRef = useRef(false);
    // Live quote of the rendered form, reported via `useReportFormQuote`; read at submit time only.
    const formQuoteRef = useRef(undefined);
    const reportFormQuote = useCallback((quote) => { formQuoteRef.current = quote; }, []);
    const [networkToConnect, setNetworkToConnect] = useState();
    const settings = useSettingsState();
    const { swapBasicData, swapDetails, swapModalOpen } = useSwapDataState();
    // The same resolved status object the Processing panel renders ("Transfer complete",
    // "Transfer failed"); it can lead the API status, so flow_closed reports from it.
    const resolved = useResolvedSwapStatus();
    const sourceNetworkWithTokens = settings.networks.find(n => n.name === swapBasicData?.source_network.name);
    const { getProvider } = useWallet(sourceNetworkWithTokens, "withdrawal");
    const { wallets: allConnectedWallets } = useWallet();
    const connectedAutofillNetworks = useMemo(() => {
        const set = new Set();
        allConnectedWallets.forEach(w => {
            w.autofillSupportedNetworks?.forEach(n => set.add(n.toLowerCase()));
        });
        return set;
    }, [allConnectedWallets]);
    const [walletWihdrawDone, setWalletWihdrawDone] = useState(false);
    const selectedSourceAccount = useSelectedAccount("from", swapBasicData?.source_network?.name);
    const { mutate: mutateBalances } = useBalance(selectedSourceAccount?.address, sourceNetworkWithTokens);
    const { onSwapLifecycle, onSwapModalStateChange } = useCallbacks();
    const { getConfirmation } = useAsyncModal();
    const initialSettings = useInitialSettings();
    const { destination_address: destinationAddressFromQuery } = initialSettings;
    const { createSwap, setSwapId, setSubmitedFormValues, setSwapModalOpen } = useSwapDataUpdate();
    const { setSwapError } = useSwapDataState();
    const { setConfirmed, isConfirmed, checkContractStatus } = useContractAddressStore();
    const handleSubmit = useCallback(async (values) => {
        // form_submitted is emitted by SwapForm before this runs.
        const lifecycleContext = lifecycleContextFromForm(values);
        setSwapError && setSwapError(null);
        useGaslessPreferenceStore.getState().clearGaslessUnavailable();
        const { destination_address, to } = values;
        setWalletWihdrawDone(false);
        if (to &&
            destination_address &&
            destinationAddressFromQuery &&
            Address.equals(destinationAddressFromQuery?.toString(), destination_address, to) &&
            !isAddressFromQueryConfirmed) {
            const provider = to && getProvider(to, 'autofill');
            const isDestAddressConnected = destination_address && provider?.connectedWallets?.some((wallet) => Address.equals(wallet.address, destination_address, to));
            const confirmed = !isDestAddressConnected ? await getConfirmation({
                content: _jsx(UrlAddressNote, { partner: partner, values: values }),
                submitText: 'Confirm address',
                dismissText: 'Cancel address'
            }) : true;
            if (confirmed) {
                setIsAddressFromQueryConfirmed(true);
            }
            else if (!confirmed) {
                onSwapLifecycle({
                    step: 'form_confirmation_cancelled',
                    stage: 'form',
                    outcome: 'cancelled',
                    path: 'DestinationAddressConfirmation',
                    reasonCode: 'destination_address_confirmation_cancelled',
                    ...lifecycleContext,
                });
                return;
            }
        }
        if (destination_address && values.from && values.to && values.to.type === 'evm') {
            const alreadyConfirmed = isConfirmed(destination_address, values.to.name);
            if (!alreadyConfirmed) {
                const { isContractInAnyNetwork, destinationIsContract } = await checkContractStatus(destination_address, values.from, values.to);
                if (isContractInAnyNetwork && !destinationIsContract) {
                    dontShowContractWarningRef.current = false;
                    const handleDontShowAgainChange = (checked) => {
                        dontShowContractWarningRef.current = checked;
                    };
                    const confirmed = await getConfirmation({
                        content: _jsx(ContractAddressNote, { values: values, onDontShowAgainChange: handleDontShowAgainChange }),
                        submitText: 'Confirm',
                        dismissText: 'Cancel'
                    });
                    if (confirmed && dontShowContractWarningRef.current) {
                        setConfirmed(destination_address, values.to.name);
                    }
                    else if (!confirmed) {
                        onSwapLifecycle({
                            step: 'form_confirmation_cancelled',
                            stage: 'form',
                            outcome: 'cancelled',
                            path: 'ContractAddressConfirmation',
                            reasonCode: 'contract_address_confirmation_cancelled',
                            ...lifecycleContext,
                        });
                        return;
                    }
                }
            }
        }
        const avgCompletionTime = formQuoteRef.current?.avg_completion_time;
        if (type !== 'deposit-address' && isVerySlowRoute(avgCompletionTime)) {
            const confirmed = await getConfirmation({
                content: _jsx(SlowRouteNote, { avgCompletionTime: avgCompletionTime }),
                submitText: 'Continue',
                dismissText: 'Choose another route'
            });
            if (!confirmed) {
                onSwapLifecycle({
                    step: 'form_confirmation_cancelled',
                    stage: 'form',
                    outcome: 'cancelled',
                    path: 'SlowRouteConfirmation',
                    reasonCode: 'slow_route_confirmation_cancelled',
                    ...lifecycleContext,
                });
                return;
            }
        }
        try {
            await handleCreateSwap({
                setSwapId,
                values,
                setSubmitedFormValues,
                query: initialSettings,
                partner,
                createSwap: async (...props) => {
                    const response = await createSwap(...props);
                    return response;
                },
                setShowSwapModal: handleShowSwapModal,
                setNetworkToConnect,
                setShowConnectNetworkModal,
                type,
            });
        }
        catch (error) {
            setSwapError && setSwapError(error?.message || 'Could not create swap');
        }
    }, [createSwap, initialSettings, partner, swapBasicData, getProvider, settings, type, setSwapError, onSwapLifecycle]);
    // Formik has no `enableReinitialize`, so this is read once at mount — memoize
    // to keep post-mount re-renders (wallet events, balance revalidation) from
    // rerunning the extended-route merge and per-token route-plan resolution.
    const initialValues = useMemo(() => swapBasicData
        ? generateSwapInitialValuesFromSwap(swapBasicData, swapBasicData.refuel, settings, type)
        : generateSwapInitialValues(settings, initialSettings, type, connectedAutofillNetworks), [swapBasicData, settings, initialSettings, type, connectedAutofillNetworks]);
    const handleShowSwapModal = useCallback((value) => {
        setSwapModalOpen(value);
        onSwapModalStateChange(value);
        if (!value) {
            if (swapBasicData) {
                // Synchronous, after onSwapModalStateChange(false): the host's close handler
                // defers its cleanup so this event keeps its journey context.
                onSwapLifecycle({
                    ...resolveFlowClosedEvent(resolved, swapDetails),
                    path: 'SwapModal',
                    status: swapDetails?.status,
                    ...lifecycleContextFromSwap(swapBasicData, swapDetails),
                });
            }
            if (walletWihdrawDone) {
                // Keep the active swap's fee presentation stable through submission.
                // The next form attempt gets the default after this modal closes.
                useGaslessPreferenceStore.getState().resetGaslessPreference();
                mutateBalances();
                setWalletWihdrawDone(false);
            }
        }
    }, [swapBasicData, swapDetails, resolved, walletWihdrawDone, mutateBalances, onSwapLifecycle, onSwapModalStateChange, setSwapModalOpen]);
    return _jsx(_Fragment, { children: _jsx(SwapForm, { mode: type, submitPath: "SwapForm", initialValues: initialValues, validateOnMount: true, onSubmit: handleSubmit, children: ({ setFieldValue, setValues, values }) => (_jsxs(_Fragment, { children: [_jsx(VaulDrawer, { show: showConnectNetworkModal, setShow: setShowConnectNetworkModal, header: `${networkToConnect?.DisplayName} connect`, modalId: "showNetwork", children: _jsx(VaulDrawer.Snap, { id: "item-1", children: networkToConnect &&
                                _jsx(ConnectNetwork, { NetworkDisplayName: networkToConnect?.DisplayName, AppURL: networkToConnect?.AppURL }) }) }), _jsx(VaulDrawer, { mode: "fitHeight", show: swapModalOpen, setShow: handleShowSwapModal, header: 'Complete the swap', modalId: "showSwap", className: "expandContainerHeight", children: swapModalOpen ? (_jsx(Suspense, { fallback: _jsx(SwapDetailsSceleton, {}), children: _jsx(SwapDetails, { type: "contained", partner: partner, onCancelWithdrawal: () => handleShowSwapModal(false), onAdjustAmount: updates => {
                                    const nextValues = { ...values, ...updates };
                                    void setValues(nextValues, true);
                                    setSubmitedFormValues(nextValues);
                                }, onWalletWithdrawalSuccess: () => {
                                    setWalletWihdrawDone(true);
                                    setFieldValue('amountMode', 'manual');
                                    setFieldValue('amountPresetPending', false);
                                    setFieldValue('amountPresetError', undefined);
                                    setFieldValue('amount', 0);
                                    mutateBalances();
                                } }) })) : null }), _jsx(FormQuoteReportProvider, { value: reportFormQuote, children: children }), _jsx(ContractAddressValidationCache, { source_network: values.from, destination_network: values.to, address: values.destination_address }), _jsx(ContractSourceAddressValidationCache, { source_network: values.from, destination_network: values.to })] })) }) });
}
const handleCreateSwap = async ({ query, values, partner, setShowSwapModal, createSwap, setNetworkToConnect, setShowConnectNetworkModal, setSwapId, setSubmitedFormValues, type }) => {
    setSubmitedFormValues(values);
    if (values.depositMethod == 'wallet') {
        setSwapId(undefined);
        setShowSwapModal(true);
        return;
    }
    try {
        const swap = await createSwap(values, query, partner);
        setSwapId(swap.swap.id);
        if (type !== 'deposit-address') {
            setShowSwapModal(true);
        }
    }
    catch (error) {
        const data = error?.response?.data?.error;
        if (data?.code === LSAPIKnownErrorCode.BLACKLISTED_ADDRESS) {
            throw new Error("You can't transfer to that address. Please double check.");
        }
        else if (data?.code === LSAPIKnownErrorCode.INVALID_ADDRESS_ERROR) {
            throw new Error(`Enter a valid ${values.to?.display_name} address`);
        }
        else if (data?.code === LSAPIKnownErrorCode.UNACTIVATED_ADDRESS_ERROR && values.to) {
            setNetworkToConnect({
                DisplayName: values.to.display_name,
                AppURL: data.metadata.ActivationUrl
            });
            setShowConnectNetworkModal(true);
        }
        else if (data?.code === LSAPIKnownErrorCode.NETWORK_CURRENCY_DAILY_LIMIT_REACHED) {
            if (data.metadata.AvailableTransactionAmount) {
                throw new Error(`Daily limit of ${values.fromAsset?.asset} transfers from ${values.from?.display_name} is reached. Please try sending up to ${data.metadata.AvailableTransactionAmount} ${values.fromAsset?.asset}.`);
            }
            else {
                throw new Error(`Daily limit of ${values.fromAsset?.asset} transfers from ${values.from?.display_name} is reached.`);
            }
        }
        else if (data?.code === "QUOTE_REQUIRES_NO_DEPOSIT_ADDRESS") {
            throw new Error("This route isn't available with a deposit address. Try a different source or destination.");
        }
        else {
            throw new Error(data?.message || error?.message);
        }
    }
};
